import {ApiUrls} from 'shared/config/model/urls';

import {createApi} from '@reduxjs/toolkit/query/react';
import {AxiosRequestHeaders} from 'axios';

import IDomainIncident, {IDomainIncidentDiff} from 'features/domain/model/DomainIncident/IDomainIncident';
import {ISummaryDomainIncident} from 'features/domain/model/DomainIncident/ISummaryDomainIncident';
import IPagination from 'shared/api/model/IPagination';
import IHydraResponse from 'shared/api/model/HydraResponse';
import ICollectionParams from 'shared/api/model/ICollectionParams';
import httpBaseQuery from 'shared/api/rtk-query/httpBaseQuery';
import {providesList} from 'shared/api/rtk-query/RTKQueryHelper';
import {paginationAdapter} from 'shared/api/paginationHelper';
import {buildQuery} from 'shared/api/query';

export const domainIncidentAPI = createApi({
    reducerPath: 'domainIncidentAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['DomainIncident', 'SummaryDomainIncident'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        getDomainIncidentsAll: build.query<IPagination<IDomainIncident>, ICollectionParams>({
            query: ({page, sort, filters, limit}) => ({
                path: buildQuery(ApiUrls.DOMAIN_INCIDENT_ALL(page, sort, filters, limit)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesList(result?.items, 'DomainIncident'),
            transformResponse(
                rawResult: IHydraResponse<IDomainIncident>,
            ): Promise<IPagination<IDomainIncident>> | IPagination<IDomainIncident> {
                return paginationAdapter(rawResult);
            },
        }),
        getSummaryDomainIncident: build.query<ISummaryDomainIncident, void>({
            query: () => ({
                path: ApiUrls.SUMMARY_DOMAIN_INCIDENTS,
            }),
            providesTags: ['SummaryDomainIncident'],
        }),
        getDomainIncident: build.query<IDomainIncident, number>({
            query: (id) => ({
                path: ApiUrls.DOMAIN_INCIDENT(id),
            }),
            providesTags: (result, error, id) => [{ type: 'DomainIncident', id }],
        }),
        updateDomainIncident: build.mutation<IDomainIncident, Partial<IDomainIncident>>({
            query: ({id, ...data}) => ({
                path: ApiUrls.DOMAIN_INCIDENT(id ?? 0),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ id, ...patch }, { dispatch }) {
                dispatch(
                    domainIncidentAPI.util.updateQueryData('getDomainIncident', id ?? 0, (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: [{ type: 'DomainIncident', id: 'LIST' }],
        }),
        getDomainIncidentDiff: build.query<IDomainIncidentDiff, number>({
            query: (id) => ({
                path: ApiUrls.DOMAIN_INCIDENT_DIFF(id),
            }),
            providesTags: (result, error, id) => [{ type: 'DomainIncident', id: `${id}-diff` }],
        }),
        saveExpectedRobots: build.mutation<void, number>({
            query: (id) => ({
                path: ApiUrls.DOMAIN_INCIDENT_SAVE_EXPECTED(id),
                method: 'POST',
                data: {},
            }),
            invalidatesTags: (result, error, id) => [
                { type: 'DomainIncident', id: `${id}-diff` },
                { type: 'DomainIncident', id },
            ],
        }),
    }),
});